import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Package, LogOut, Edit, RefreshCw, AlertTriangle, CheckCircle2, Plus, Mail, X, Save, Upload, Image, Pencil, Link } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [staffInfo, setStaffInfo] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('inventory');
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  // Add Product State
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    slug: '',
    category: 'Metabolic',
    short_description: '',
    description: '',
    purity: '≥99%',
    featured: false,
    variants: [{ size: '5mg', price: 0, stock_quantity: 0, in_stock: true }]
  });
  const [productImage, setProductImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [addingProduct, setAddingProduct] = useState(false);

  // Edit Product State
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductData, setEditProductData] = useState(null);
  const [editProductImage, setEditProductImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [useExternalImageUrl, setUseExternalImageUrl] = useState(false);
  const [externalImageUrl, setExternalImageUrl] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/me`, {  });
      if (response.ok) {
        const data = await response.json();
        setStaffInfo(data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/staff/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(loginForm)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Login failed');
      }

      const data = await response.json();
      setStaffInfo(data.staff);
      setIsAuthenticated(true);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/staff/logout`, {
        method: 'POST',
        
      });
      setIsAuthenticated(false);
      setStaffInfo(null);
      toast.success('Logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products`, {  });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleUpdateStock = async (productId, variantIndex, newQuantity, inStock) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}/variant/${variantIndex}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ stock_quantity: newQuantity, in_stock: inStock })
      });

      if (!response.ok) throw new Error('Update failed');
      
      toast.success('Stock updated');
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setAddingProduct(true);

    try {
      // Use FormData to send file
      const formData = new FormData();
      formData.append('name', newProduct.name);
      formData.append('slug', newProduct.slug || generateSlug(newProduct.name));
      formData.append('category', newProduct.category);
      formData.append('short_description', newProduct.short_description);
      formData.append('description', newProduct.description);
      formData.append('purity', newProduct.purity);
      formData.append('featured', newProduct.featured);
      
      // Add first variant info
      if (newProduct.variants.length > 0) {
        formData.append('variant_size', newProduct.variants[0].size);
        formData.append('variant_price', newProduct.variants[0].price);
        formData.append('variant_stock', newProduct.variants[0].stock_quantity);
      }
      
      // Add additional variants as JSON
      if (newProduct.variants.length > 1) {
        formData.append('additional_variants', JSON.stringify(newProduct.variants.slice(1)));
      }
      
      // Add image if selected
      if (productImage) {
        formData.append('image', productImage);
      }

      const response = await fetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add product');
      }

      toast.success('Product added successfully!');
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        slug: '',
        category: 'Metabolic',
        short_description: '',
        description: '',
        purity: '≥99%',
        featured: false,
        variants: [{ size: '5mg', price: 0, stock_quantity: 0, in_stock: true }]
      });
      setProductImage(null);
      setImagePreview(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleNotifyCustomers = async (productId, productName) => {
    if (!window.confirm(`Send "Back in Stock" email to ALL customers about "${productName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}/notify-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_url: window.location.origin })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send notifications');
      }

      toast.success(`📧 Email sent to ${data.sent_count} customers!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Edit Product Functions
  const openEditProduct = (product) => {
    setEditingProduct(product.product_id);
    setEditProductData({
      name: product.name || '',
      slug: product.slug || '',
      category: product.category || 'Metabolic',
      short_description: product.short_description || '',
      description: product.description || '',
      purity: product.purity || '≥99%',
      featured: product.featured || false,
      variants: product.variants || [{ size: '5mg', price: 0, stock_quantity: 0, in_stock: true }]
    });
    setEditImagePreview(product.image_url ? 
      (product.image_url.startsWith('/') ? `${API_URL}${product.image_url}` : product.image_url) 
      : null
    );
    setEditProductImage(null);
    setUseExternalImageUrl(false);
    setExternalImageUrl('');
    setActiveTab('edit-product');
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setEditProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);

    try {
      const formData = new FormData();
      formData.append('name', editProductData.name);
      formData.append('slug', editProductData.slug || generateSlug(editProductData.name));
      formData.append('category', editProductData.category);
      formData.append('short_description', editProductData.short_description);
      formData.append('description', editProductData.description);
      formData.append('purity', editProductData.purity);
      formData.append('featured', editProductData.featured);
      
      // Send variants as JSON
      formData.append('variants', JSON.stringify(editProductData.variants));
      
      if (useExternalImageUrl && externalImageUrl.trim()) {
        formData.append('external_image_url', externalImageUrl.trim());
      } else if (editProductImage) {
        formData.append('image', editProductImage);
      }

      const response = await fetch(`${API_URL}/api/admin/products/${editingProduct}/full`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update product');
      }

      toast.success('Product updated successfully!');
      setEditingProduct(null);
      setEditProductData(null);
      setEditProductImage(null);
      setEditImagePreview(null);
      setUseExternalImageUrl(false);
      setExternalImageUrl('');
      setActiveTab('inventory');
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setEditProductData(null);
    setEditProductImage(null);
    setEditImagePreview(null);
    setUseExternalImageUrl(false);
    setExternalImageUrl('');
    setActiveTab('inventory');
  };

  const updateEditVariant = (index, field, value) => {
    setEditProductData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const addEditVariant = () => {
    setEditProductData(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', price: 0, stock_quantity: 0, in_stock: true }]
    }));
  };

  const removeEditVariant = (index) => {
    if (editProductData.variants.length > 1) {
      setEditProductData(prev => ({
        ...prev,
        variants: prev.variants.filter((_, i) => i !== index)
      }));
    }
  };

  const addVariant = () => {
    setNewProduct(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', price: 0, stock_quantity: 0, in_stock: true }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setNewProduct(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const removeVariant = (index) => {
    if (newProduct.variants.length > 1) {
      setNewProduct(prev => ({
        ...prev,
        variants: prev.variants.filter((_, i) => i !== index)
      }));
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-teal-700" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Staff Portal</h1>
              <p className="text-slate-500">Genora LLC Inventory Management</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="mt-1"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-teal-400" />
              <div>
                <h1 className="font-semibold text-sm sm:text-base">Staff Portal</h1>
                <p className="text-xs sm:text-sm text-slate-400">Welcome, {staffInfo?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchProducts}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 text-xs sm:text-sm"
              >
                <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 text-xs sm:text-sm"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('inventory')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'inventory' 
                  ? 'border-teal-600 text-teal-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Inventory
            </button>
            <button
              onClick={() => setActiveTab('add-product')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'add-product' 
                  ? 'border-teal-600 text-teal-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Add Product
            </button>
            {editingProduct && (
              <button
                onClick={() => setActiveTab('edit-product')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'edit-product' 
                    ? 'border-amber-600 text-amber-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Pencil className="h-4 w-4 inline mr-1" />
                Edit Product
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        
        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">Product Inventory</h2>
              <p className="text-slate-500 text-sm sm:text-base">Update stock quantities and notify customers</p>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No products found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <Card key={product.product_id} className="border-slate-200">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                        {product.image_url && (
                          <img 
                            src={product.image_url.startsWith('/') ? `${API_URL}${product.image_url}` : product.image_url}
                            alt={product.name}
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-slate-900 truncate">{product.name}</h3>
                              <p className="text-sm text-slate-500">{product.category}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditProduct(product)}
                                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                title="Edit product"
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Edit</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleNotifyCustomers(product.product_id, product.name)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                title="Email all customers about this product"
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                <span className="hidden sm:inline">Notify</span>
                              </Button>
                            </div>
                          </div>
                          
                          <div className="mt-3 space-y-2">
                            {product.variants?.map((variant, index) => (
                              <div 
                                key={index} 
                                className={`p-2 sm:p-3 rounded-lg ${
                                  variant.in_stock && variant.stock_quantity > 0 
                                    ? 'bg-green-50' 
                                    : 'bg-red-50'
                                }`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div className="flex items-center gap-2 sm:gap-3">
                                    <span className="font-medium text-sm sm:text-base">{variant.size}</span>
                                    <span className="text-slate-500 text-sm">${variant.price}</span>
                                    {variant.in_stock && variant.stock_quantity > 0 ? (
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <AlertTriangle className="h-4 w-4 text-red-500" />
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="number"
                                        min="0"
                                        value={variant.stock_quantity || 0}
                                        onChange={(e) => {
                                          const newQty = parseInt(e.target.value) || 0;
                                          handleUpdateStock(
                                            product.product_id, 
                                            index, 
                                            newQty, 
                                            newQty > 0
                                          );
                                        }}
                                        className="w-16 sm:w-20 text-center text-sm"
                                      />
                                      <span className="text-xs sm:text-sm text-slate-500">units</span>
                                    </div>
                                    
                                    <Button
                                      size="sm"
                                      variant={variant.in_stock ? "outline" : "default"}
                                      onClick={() => handleUpdateStock(
                                        product.product_id,
                                        index,
                                        variant.stock_quantity,
                                        !variant.in_stock
                                      )}
                                      className={`text-xs sm:text-sm ${variant.in_stock ? '' : 'bg-green-600 hover:bg-green-700'}`}
                                    >
                                      {variant.in_stock ? 'Mark Out' : 'Mark In'}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Add Product Tab */}
        {activeTab === 'add-product' && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">Add New Product</h2>
              <p className="text-slate-500 text-sm">Create a new product listing</p>
            </div>

            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleAddProduct} className="space-y-6">
                  {/* Product Image */}
                  <div>
                    <Label>Product Image</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                      />
                      
                      {imagePreview ? (
                        <div className="relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setProductImage(null);
                              setImagePreview(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-teal-500 hover:text-teal-500 transition-colors"
                        >
                          <Upload className="h-8 w-8 mb-2" />
                          <span className="text-xs">Upload Image</span>
                        </button>
                      )}
                      
                      <p className="text-xs text-slate-500 mt-2">Max 5MB. Recommended: 500x500px</p>
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <Label>Product Name *</Label>
                      <Input
                        value={newProduct.name}
                        onChange={(e) => setNewProduct(prev => ({ 
                          ...prev, 
                          name: e.target.value,
                          slug: generateSlug(e.target.value)
                        }))}
                        placeholder="e.g., BPC-157"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>URL Slug</Label>
                      <Input
                        value={newProduct.slug}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="auto-generated-from-name"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Leave empty to auto-generate from name</p>
                    </div>

                    <div>
                      <Label>Category *</Label>
                      <select
                        value={newProduct.category}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                        required
                      >
                        <option value="Metabolic">Metabolic Research</option>
                        <option value="Cognitive">Cognitive Research</option>
                        <option value="Recovery">General Research</option>
                        <option value="Anti-Aging">Cellular Research</option>
                        <option value="Immune">Immune Research</option>
                        <option value="Sleep">Sleep Research</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label>Short Description</Label>
                      <Input
                        value={newProduct.short_description}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, short_description: e.target.value }))}
                        placeholder="Brief product summary"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Full Description</Label>
                      <textarea
                        value={newProduct.description}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Detailed product description..."
                        rows={4}
                        className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>

                    <div>
                      <Label>Purity</Label>
                      <Input
                        value={newProduct.purity}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, purity: e.target.value }))}
                        placeholder="≥99%"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Variants */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label>Product Variants *</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addVariant}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Variant
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {newProduct.variants.map((variant, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <Input
                            placeholder="Size (e.g., 5mg)"
                            value={variant.size}
                            onChange={(e) => updateVariant(index, 'size', e.target.value)}
                            className="flex-1"
                            required
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Price"
                              value={variant.price || ''}
                              onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || 0)}
                              className="w-24"
                              required
                            />
                          </div>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Stock"
                            value={variant.stock_quantity || ''}
                            onChange={(e) => updateVariant(index, 'stock_quantity', parseInt(e.target.value) || 0)}
                            className="w-20"
                          />
                          {newProduct.variants.length > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeVariant(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Featured */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={newProduct.featured}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, featured: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="featured" className="cursor-pointer">Featured Product</Label>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={addingProduct}
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                    >
                      {addingProduct ? (
                        <>Adding...</>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Add Product
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveTab('inventory')}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Product Tab */}
        {activeTab === 'edit-product' && editProductData && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">Edit Product</h2>
              <p className="text-slate-500 text-sm">Update product details</p>
            </div>

            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSaveEdit} className="space-y-6">
                  {/* Product Image */}
                  <div>
                    <Label>Product Image</Label>
                    <div className="flex gap-2 mt-1 mb-2">
                      <button
                        type="button"
                        onClick={() => setUseExternalImageUrl(false)}
                        className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 ${!useExternalImageUrl ? 'bg-teal-100 text-teal-700 font-medium' : 'bg-slate-100 text-slate-500'}`}
                      >
                        <Upload className="h-3 w-3" /> Upload File
                      </button>
                      <button
                        type="button"
                        onClick={() => setUseExternalImageUrl(true)}
                        className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 ${useExternalImageUrl ? 'bg-teal-100 text-teal-700 font-medium' : 'bg-slate-100 text-slate-500'}`}
                      >
                        <Link className="h-3 w-3" /> External URL
                      </button>
                    </div>
                    <div className="mt-2">
                      {useExternalImageUrl ? (
                        <div className="space-y-2">
                          <Input
                            placeholder="https://drive.google.com/... or any image URL"
                            value={externalImageUrl}
                            onChange={(e) => {
                              setExternalImageUrl(e.target.value);
                              if (e.target.value.trim()) setEditImagePreview(e.target.value.trim());
                            }}
                            data-testid="external-image-url-input"
                          />
                          <p className="text-xs text-slate-500">Paste a Google Drive link or any public image URL</p>
                          {editImagePreview && (
                            <img src={editImagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border border-slate-200" onError={(e) => { e.target.style.display = 'none'; }} />
                          )}
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            ref={editFileInputRef}
                            onChange={handleEditImageChange}
                            accept="image/*"
                            className="hidden"
                          />
                          {editImagePreview ? (
                            <div className="relative inline-block">
                              <img 
                                src={editImagePreview} 
                                alt="Preview" 
                                className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => editFileInputRef.current?.click()}
                                className="absolute bottom-2 right-2 bg-white text-slate-600 rounded-full p-2 shadow-lg hover:bg-slate-50"
                                title="Change image"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => editFileInputRef.current?.click()}
                              className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-teal-500 hover:text-teal-500 transition-colors"
                            >
                              <Upload className="h-8 w-8 mb-2" />
                              <span className="text-xs">Upload Image</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <Label>Product Name *</Label>
                      <Input
                        value={editProductData.name}
                        onChange={(e) => setEditProductData(prev => ({ 
                          ...prev, 
                          name: e.target.value,
                          slug: generateSlug(e.target.value)
                        }))}
                        placeholder="e.g., BPC-157"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>URL Slug</Label>
                      <Input
                        value={editProductData.slug}
                        onChange={(e) => setEditProductData(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="auto-generated-from-name"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Category *</Label>
                      <select
                        value={editProductData.category}
                        onChange={(e) => setEditProductData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                        required
                      >
                        <option value="Metabolic">Metabolic Research</option>
                        <option value="Cognitive">Cognitive Research</option>
                        <option value="Recovery">General Research</option>
                        <option value="Anti-Aging">Cellular Research</option>
                        <option value="Immune">Immune Research</option>
                        <option value="Sleep">Sleep Research</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label>Short Description</Label>
                      <Input
                        value={editProductData.short_description}
                        onChange={(e) => setEditProductData(prev => ({ ...prev, short_description: e.target.value }))}
                        placeholder="Brief product summary"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Full Description</Label>
                      <textarea
                        value={editProductData.description}
                        onChange={(e) => setEditProductData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Detailed product description..."
                        rows={4}
                        className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>

                    <div>
                      <Label>Purity</Label>
                      <Input
                        value={editProductData.purity}
                        onChange={(e) => setEditProductData(prev => ({ ...prev, purity: e.target.value }))}
                        placeholder="≥99%"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Variants */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label>Product Variants</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addEditVariant}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Variant
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {editProductData.variants.map((variant, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                          <Input
                            placeholder="Size (e.g., 5mg)"
                            value={variant.size}
                            onChange={(e) => updateEditVariant(index, 'size', e.target.value)}
                            className="flex-1"
                            required
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Price"
                              value={variant.price || ''}
                              onChange={(e) => updateEditVariant(index, 'price', parseFloat(e.target.value) || 0)}
                              className="w-24"
                              required
                            />
                          </div>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Stock"
                            value={variant.stock_quantity || ''}
                            onChange={(e) => updateEditVariant(index, 'stock_quantity', parseInt(e.target.value) || 0)}
                            className="w-20"
                          />
                          {editProductData.variants.length > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeEditVariant(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Featured */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-featured"
                      checked={editProductData.featured}
                      onChange={(e) => setEditProductData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="edit-featured" className="cursor-pointer">Featured Product</Label>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={savingEdit}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {savingEdit ? (
                        <>Saving...</>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffPage;
